import { useState } from 'react';
import { Loader2, Shield, AlertTriangle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function CountTile({ label, value, amber }) {
  return (
    <div className={`border rounded p-3 text-center ${amber && value > 0 ? 'border-amber-400 bg-amber-50' : ''}`}>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function FeedRow({ feed }) {
  const ok = feed.status === 'ok';
  return (
    <tr className="border-b last:border-b-0">
      <td className="py-1.5 pr-3 text-sm font-medium">{feed.entity_id}</td>
      <td className="py-1.5 pr-3 text-sm">
        {ok
          ? <Badge variant="default" className="text-xs bg-green-600">ok</Badge>
          : <Badge variant="destructive" className="text-xs">{feed.status}</Badge>
        }
      </td>
      <td className="py-1.5 text-sm text-right tabular-nums">{feed.txn_count}</td>
    </tr>
  );
}

function MislabelRow({ item }) {
  return (
    <tr className="border-b last:border-b-0">
      <td className="py-1.5 pr-3 text-sm font-mono">{(item.invoice_numbers || []).join(', ') || '(none)'}</td>
      <td className="py-1.5 pr-3 text-sm text-right tabular-nums">{item.amount}</td>
      <td className="py-1.5 pr-3 text-sm">{item.classification}</td>
      <td className="py-1.5 pr-3 text-sm">{item.confidence}</td>
      <td className="py-1.5 pr-3 text-sm">{item.match_field}</td>
      <td className="py-1.5 text-sm text-xs">{(item.colliding_feeds || []).join(', ')}</td>
    </tr>
  );
}

export function BiImmoReconClassificationTrustPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.biImmoReconClassificationTrust();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const byCls = result?.by_classification || {};
  const feeds = result?.owner_feed_summary || [];
  const mislabelList = result?.mislabel_risk_matches || [];
  const baseline = result?.pre_widening_baseline;
  const failedFeeds = feeds.filter(f => f.status !== 'ok');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Shield className="h-5 w-5 text-blue-600" />
          BI Immo Recon Classification Trust
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Read-only audit of same_entity / cross_entity classification trustworthiness.
          The matcher uses first-found-wins across the union pool; this card measures
          how many matches have competing transactions in multiple feeds (cross-feed
          ambiguity). Mislabel-risk must be 0 before the same_entity apply gate is safe.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={run} disabled={loading} size="sm">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Run Audit
        </Button>

        {result?.status === 'error' && (
          <div className="border rounded p-3 bg-red-50 text-sm text-red-700">
            {result.error}
          </div>
        )}

        {result?.status === 'ok' && (
          <div className="space-y-6">
            {/* Forward floor notice */}
            {result.forward_floor && (
              <div className="border border-blue-200 bg-blue-50 dark:bg-blue-950/30 rounded px-3 py-1.5 text-xs text-blue-800 dark:text-blue-300">
                Forward floor: payouts on/after {result.forward_floor}
                {result.pool_size_after_floor != null && (
                  <span className="ml-2 text-muted-foreground">
                    ({result.pool_size_after_floor} txns in floored pool)
                  </span>
                )}
              </div>
            )}

            {/* Tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <CountTile label="Total matches" value={result.total_matches} />
              <CountTile
                label="same_entity"
                value={byCls.same_entity || 0}
              />
              <CountTile
                label="cross_entity"
                value={byCls.cross_entity || 0}
              />
              <CountTile
                label="Matcher internal ambiguous"
                value={result.matcher_internal_ambiguous || 0}
                amber
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <CountTile
                label="Cross-feed ambiguous"
                value={result.ambiguous_count}
                amber
              />
              <CountTile
                label="Mislabel-risk"
                value={result.mislabel_risk_count}
                amber
              />
            </div>

            {/* Verdict */}
            <div className={`border rounded p-3 ${result.mislabel_risk_count === 0 ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-400'}`}>
              <p className="text-sm font-semibold flex items-center gap-1">
                {result.mislabel_risk_count === 0
                  ? <><CheckCircle className="h-4 w-4 text-green-600" /> Classification is trustworthy</>
                  : <><AlertTriangle className="h-4 w-4 text-amber-600" /> Classification has mislabel risk</>
                }
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {result.mislabel_risk_count === 0
                  ? 'No matches have competing transactions across feeds with asserted (non-ambiguous) classification. The same_entity apply gate can trust the label.'
                  : `${result.mislabel_risk_count} match(es) have asserted classification despite cross-feed amount collision. Review the list below before wiring the same_entity apply gate.`
                }
              </p>
            </div>

            {/* Pre-widening baseline */}
            {baseline && (
              <div className="border rounded p-3 bg-muted/50 text-sm space-y-1">
                <p className="font-medium">Pre-widening baseline</p>
                <p className="text-xs text-muted-foreground">{baseline.note}</p>
                <div className="grid grid-cols-2 gap-x-4 text-sm mt-1">
                  <div>BI Immo feed involved:</div>
                  <div className="font-mono">{baseline.bi_immo_feed_involved}</div>
                  <div>Owner-only collisions:</div>
                  <div className="font-mono">{baseline.owner_only_collisions}</div>
                </div>
              </div>
            )}

            {/* Owner feed pool */}
            <div>
              <h3 className="text-sm font-semibold mb-1">Owner feed pool</h3>
              {failedFeeds.length > 0 && (
                <div className="text-xs text-amber-600 mb-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {failedFeeds.length} feed(s) failed to fetch (not silently read as "no payout")
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b text-xs text-muted-foreground">
                      <th className="py-1 pr-3">Entity</th>
                      <th className="py-1 pr-3">Status</th>
                      <th className="py-1 text-right">Txns</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feeds.map(f => <FeedRow key={f.entity_id} feed={f} />)}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mislabel-risk list */}
            {mislabelList.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-1 text-amber-700">Mislabel-risk matches</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b text-xs text-muted-foreground">
                        <th className="py-1 pr-3">Invoice(s)</th>
                        <th className="py-1 pr-3 text-right">Amount</th>
                        <th className="py-1 pr-3">Classification</th>
                        <th className="py-1 pr-3">Confidence</th>
                        <th className="py-1 pr-3">Match field</th>
                        <th className="py-1">Colliding feeds</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mislabelList.map((item, i) => <MislabelRow key={i} item={item} />)}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Timestamp */}
            <p className="text-xs text-muted-foreground">
              Checked at: {result.checked_at} (lookback: {result.days_back}d)
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
